import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  DeleteRecord,
  GetListview,
  SuccessAlert,
  URLPRIFIX,
  ErrorAlert,
  GetStatusCounts
} from "../../functions/api";
import { useStore } from "../../Components/StateProvider";
import Pagination from "../../Components/Fields/Pagination";
import StatusTabs from "../../Components/Elements/StatusTabs";
import BulkActionsBar from "../../Components/Elements/BulkActionsBar";
import ListViewUp from "../../Components/Elements/ListViewUp";

const UserListUpd = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tablename = "users";
  const tableURL = "/users/getusers";
  const DeleteURL = "/users/deleteusers/";
  const statusCountURL = "/users/getuserstatuscount";
  const [activeTab, setActiveTab] = useState("ALL");
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [hasTabClicked, setHasTabClicked] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    field: "",
    direction: "asc",
  });
  const [statusTabs, setStatusTabs] = useState([]);

  const Tablehead =
    " pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[16px] xl:text-[20px] font-bold ";
  const Tablerecord =
    " pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[12px] xl:text-[16px] overflow-hidden";

  const header = [
    ["First Name", "firstName"],
    ["Last Name", "lastName"],
    ["User Name", "userName"],
    ["E-Mail", "email"],
    ["Action", "ACTION"],
  ];

  const store = useStore();
  const init = {
    filterfield: "all",
    search: "",
    sort: "desc",
    currentpage: 1,
    totalpage: 1,
    pagesize: 20,
  };

  const fetchStatusCounts = useCallback(async () => {
    store.startLoading();
    try {
      const counts = await GetStatusCounts(statusCountURL, store);
      if (counts && counts.statuscount) {
        setStatusTabs(counts.statuscount);
      }
    } catch (error) {
      ErrorAlert("Failed to fetch status counts.");
      console.error("Error fetching status counts:", error);
    } finally {
      store.stopLoading();
    }
  }, [store, statusCountURL]);

  const loadListview = useCallback((params = {}) => {
    const currentState = store?.state?.records?.nonlitigation_projects || init;
    GetListview({
      listUrl: tableURL,
      store: store,
      field: params.field !== undefined ? params.field : store.getvalue("filterfield"),
      search: params.search !== undefined ? params.search : store.getvalue("search"),
      sort: params.sort !== undefined ? params.sort : store.getvalue("sort"),
      page: params.page !== undefined ? params.page : store.getvalue("currentpage"),
      pagesize: params.pagesize !== undefined ? params.pagesize : store.getvalue("pagesize"),
    });
  }, []);

  useEffect(() => {
    store.setmodule(tablename, init).then(() => {
      store.stopLoading();
      store.setheader(tablename + " List");
      store.setvalue("defaultpagesize", init.pagesize);
      fetchStatusCounts();
    });
    loadListview({
      field: 'all',
      search: '',
      sort: 'desc',
      page: 1,
      pagesize: '20',
    });
  }, []);

  const change = useCallback((props) => {
    const updatedFilters = {};
    if (props.search !== undefined && props.field !== undefined) {
      updatedFilters[props.field] = props.search;
    } else if (props.field === "status") {
      updatedFilters.status = props.search;
    }
    setFilters(updatedFilters);
    store.setvalue("filters", updatedFilters);
    const field = props.field || "all";
    const search = props.search || "";
    const pagesize = props.pagesize || store.getvalue("defaultpagesize");
    const sort = props.sort || init.sort;
    store.setvalue("filterfield", field);
    store.setvalue("search", search);
    store.setvalue("pagesize", pagesize);
    store.setvalue("sort", sort);
    store.setvalue("currentpage", 1);
    loadListview({
      field,
      search,
      sort,
      page: 1,
      pagesize,
    });
  }, []);
  const onPageChange = useCallback((page, pagesize = store.getvalue("pagesize")) => {
    loadListview({
      field: store.getvalue("filterfield"),
      search: store.getvalue("search"),
      sort: store.getvalue("sort"),
      page: page,
      pagesize: pagesize,
    });
  }, []);


  const handleOnSelect = useCallback((itemOrItems, isChecked) => {
    if (Array.isArray(itemOrItems)) {
      if (isChecked) {
        setSelectedRecords([...itemOrItems]);
      } else {
        setSelectedRecords([]);
      }
    } else {
      if (isChecked) {
        setSelectedRecords((prevSelected) => [...prevSelected, itemOrItems]);
      } else {
        setSelectedRecords((prevSelected) =>
          prevSelected.filter((record) => record.id !== itemOrItems.id)
        );
      }
    }
  }, []);

  const handleBulkDelete = async () => {
    const confirmBulkDelete = window.confirm(
      `Are you sure you want to delete ${selectedRecords.length} selected items?`
    );
    if (!confirmBulkDelete) {
      return;
    }

    store.startLoading();
    let allSuccess = true;
    for (const item of selectedRecords) {
      try {
        const res = await DeleteRecord(DeleteURL + item.id, {
          modifiedBy: store.getuser().id,
        });
        if (!res.success) {
          allSuccess = false;
          ErrorAlert(
            res?.errormessage?.response?.data ||
            `Error deleting item ${item.id}. Please contact the Admin.`
          );
        }
      } catch (error) {
        allSuccess = false;
        ErrorAlert(`Error deleting item ${item.id}: ${error.message}`);
      }
    }

    if (allSuccess) {
      SuccessAlert("Selected items deleted successfully!");
    } else {
      ErrorAlert("Some items could not be deleted. Check console for details.");
    }

    setSelectedRecords([]);
    loadListview();
    fetchStatusCounts();
    store.stopLoading();
  };

  const handleDelete = useCallback(async (item) => {
    const confirmDelete = window.confirm("Are you sure you want to delete?");
    if (!confirmDelete) {
      return;
    }
    store.startLoading();
    try {
      const res = await DeleteRecord(DeleteURL + item.id, {
        modifiedBy: store.getuser().id,
      });
      if (res.success) {
        SuccessAlert("Deleted Successfully");
        setSelectedRecords((prevSelected) =>
          prevSelected.filter((record) => record.id !== item.id)
        );
        loadListview();
        fetchStatusCounts();
      } else {
        ErrorAlert(
          res?.errormessage?.response?.data ||
          "Error Found. Please contact the Admin."
        );
      }
    } catch (error) {
      ErrorAlert(`Error deleting item: ${error.message}`);
    } finally {
      store.stopLoading();
    }
  }, [DeleteURL, store, fetchStatusCounts, loadListview]);


  const handleClickRow = useCallback((item) => {
    if (store.getprops("view")) {
      store.navTo(`/${URLPRIFIX}/${tablename}/view/${item.id}`);
    }
  }, [store, tablename]);

  const handleSort = useCallback((label) => {
    const newDirection =
      sortConfig.field === label && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({
      field: label,
      direction: newDirection,
    });

    change({
      field: store.getvalue("filterfield"),
      search: store.getvalue("search"),
      sort: newDirection,
      pagesize: store.getvalue("pagesize"),
    });
  }, [change, sortConfig, store]);

  const handleFilterChange = useCallback((label, newValue) => {
    console.log("FilterNL", label, newValue, filters);
    const updatedFilters = { ...filters, [label]: newValue };
    setFilters(updatedFilters);
    change({
      field: label,
      search: newValue,
      pagesize: store.getvalue("pagesize"),
      sort: sortConfig.direction,
    });
  }, [change, filters, sortConfig.direction, store]);


  const records = store.getvalue("list") || [];

  const filteredRecords = records
    .filter((record) => {
      if (activeTab !== "ALL" && record.status?.toUpperCase() !== activeTab) {
        return false;
      }
      return header.every(([_, label]) => {
        const filterValue = filters[label];
        if (!filterValue) return true;

        let fieldValue = record[label];
        if (label.toLowerCase().includes("date") && fieldValue) {
          const parsed = new Date(fieldValue);
          if (!isNaN(parsed)) fieldValue = parsed.toISOString().split("T")[0];
        }
        return (
          fieldValue &&
          fieldValue.toString().toLowerCase().includes(filterValue.toLowerCase())
        );
      });
    })
    .sort((a, b) => {
      const { field, direction } = sortConfig;
      if (!field) return 0;

      const aVal = (a[field] ?? "").toString().toLowerCase();
      const bVal = (b[field] ?? "").toString().toLowerCase();

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

  const handleSelectAll = useCallback((event) => {
    if (event.target.checked) {
      handleOnSelect(filteredRecords, true);
    } else {
      handleOnSelect([], false);
    }
  }, [filteredRecords, handleOnSelect]);

  const isAllSelected =
    filteredRecords.length > 0 &&
    selectedRecords.length === filteredRecords.length &&
    filteredRecords.every((record) =>
      selectedRecords.some((sItem) => sItem.id === record.id)
    );

  const isSelected = useCallback((item) => selectedRecords.some((sItem) => sItem.id === item.id), [selectedRecords]);

  const handleTabClick = useCallback((status, count) => {
    setActiveTab(status);
    setHasTabClicked(true);
    store.setvalue("activeTab", status);
    store.setvalue("tabclicked", true);
    const isAll = status === "ALL";

    // Update pagesize in store for API call
    const pageSize = count;
    store.setvalue("pagesize", pageSize);

    // Call change with the updated parameters
    change({
      field: isAll ? "" : "status",
      search: isAll ? "" : status,
      pagesize: pageSize,
      sort: store.getvalue("sort"),
    });

    // Update filters for local UI
    setFilters(isAll ? {} : { status: status });
    store.setvalue("filters", isAll ? {} : { status: status });
  }, [change, store]);

  const handleClearFilter = useCallback(() => {
    setActiveTab("ALL");
    setFilters({});
    setHasTabClicked(false);
    store.setvalue("filters", {});
    store.setvalue("pagesize", init.pagesize);
    store.setvalue("filterfield", "all");
    store.setvalue("search", "");
    store.setvalue("sort", init.sort);
    store.setvalue("currentpage", 1);

    loadListview({
      field: "",
      search: "",
      sort: init.sort,
      page: 1,
      pagesize: init.pagesize,
    });
  }, [init.pagesize, init.sort, loadListview, store]);


  return (
    <div className="grid grid-rows-10 h-full">
      <div className="flex flex-wrap justify-between items-center mb-2 px-2">
        <StatusTabs
          statusTabs={statusTabs}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          hasTabClicked={hasTabClicked}
          onClearClick={handleClearFilter}
        />

        {store.getprops("add") && (
          <button
            className="bg-primary text-secondary px-3 py-1 rounded-full border-2 transition text-sm"
            onClick={() => {
              store.removemodule("nonlitigation_project");
              store.navTo("/demo/" + tablename + "/create");
            }}
          >
            ➕ ADD NEW
          </button>
        )}
      </div>

      <div className="row-span-9 h-full w-full">
        <div className="py-5 px-[30px] h-full w-full" contentEditable={false}>
          <ListViewUp
            header={header}
            filteredRecords={filteredRecords}
            Tablehead={Tablehead}
            Tablerecord={Tablerecord}
            sortConfig={sortConfig}
            handleSort={handleSort}
            filters={filters}
            onFilterChange={handleFilterChange}
            store={store}
            handleOnSelect={handleOnSelect}
            isSelected={isSelected}
            handleClickRow={handleClickRow}
            handleDelete={handleDelete}
            handleSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
            tablename={tablename}
          />
          <BulkActionsBar
            selectedRecords={selectedRecords}
            store={store}
            onBulkDelete={handleBulkDelete}
          />
          <div className="float-right mr-8 mt-6">
            <Pagination onchange={onPageChange} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserListUpd;


